import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import ShareMenu from '../components/ShareMenu';
import { repository } from '../lib/repository';

export default function JournalArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => {
    repository.getArticle(slug).then((a) => (a ? setArticle(a) : nav('/journal')));
    repository.listJournal().then(setAll);
  }, [slug, nav]);

  // Related: same category first, then shared tags, exclude self.
  const related = useMemo(() => {
    if (!article) return [];
    const others = all.filter((a) => a.slug !== article.slug);
    const scored = others.map((a) => {
      const tagOverlap = a.tags.filter((t) => article.tags.includes(t)).length;
      const catMatch = a.category === article.category ? 3 : 0;
      return { a, score: catMatch + tagOverlap };
    });
    scored.sort((x, y) => y.score - x.score);
    return scored.slice(0, 3).map((x) => x.a);
  }, [article, all]);

  if (!article) return <div className="container py-32" />;

  return (
    <article data-testid={`page-journal-article-${article.slug}`}>
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/journal/${article.slug}`}
        image={article.cover}
        type="article"
        keywords={article.tags}
        breadcrumb={[
          { to: '/', label: 'Home' },
          { to: '/journal', label: 'Journal' },
          { label: article.title },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          image: [article.cover],
          datePublished: article.date,
          dateModified: article.date,
          author: article.author ? { '@type': 'Person', name: article.author.name, jobTitle: article.author.role } : undefined,
          publisher: { '@type': 'Organization', name: 'Evermark' },
          articleSection: article.category,
          keywords: article.tags.join(', '),
        }}
      />
      <div className="relative h-[70vh] min-h-[500px] overflow-hidden border-b border-border">
        <img src={article.cover} alt={article.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
          <p className="overline">
            {article.category} · {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {article.readTime}
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.85, 0, 0.15, 1] }}
            className="mt-6 font-display text-5xl md:text-8xl leading-[0.9] max-w-4xl"
          >
            {article.title}
          </motion.h1>
          <p className="mt-4 font-display italic text-2xl">{article.subtitle}</p>
        </div>
      </div>

      <div className="container pt-12 pb-4">
        <Breadcrumb items={[
          { to: '/', label: 'Home' },
          { to: '/journal', label: 'Journal' },
          { label: article.title },
        ]} />
      </div>

      <div className="container pb-24 md:pb-32 grid md:grid-cols-[220px_1fr] gap-12">
        {/* Byline / meta rail */}
        <aside className="md:sticky md:top-32 md:self-start">
          {article.author && (
            <>
              <p className="overline text-muted-foreground">Written by</p>
              <p className="mt-2 font-display text-2xl">{article.author.name}</p>
              <p className="overline text-muted-foreground">{article.author.role}</p>
            </>
          )}
          <div className="mt-6">
            <p className="overline text-muted-foreground mb-2">Tags</p>
            <div className="flex flex-wrap gap-1">
              {article.tags.map((t) => (
                <Link key={t} to={`/journal?tag=${t}`} className="text-xs px-2 py-1 border border-border hover:border-foreground transition-colors duration-500">
                  #{t}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-6">
            <ShareMenu title={`${article.title} — Evermark Journal`} />
          </div>
        </aside>

        {/* Body */}
        <div className="max-w-2xl">
          {article.body.map((para, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className={`text-lg md:text-xl leading-[1.75] text-foreground/85 ${i === 0 ? '' : 'mt-8'} ${i === 0 ? 'first-letter:font-display first-letter:text-7xl first-letter:mr-2 first-letter:float-left first-letter:leading-none' : ''}`}
            >
              {para}
            </motion.p>
          ))}
          <div className="brushline my-16" />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link to="/journal" className="overline inline-flex items-center gap-2 link-underline">
              <ArrowLeft className="h-3 w-3" /> Back to Journal
            </Link>
            <ShareMenu title={`${article.title} — Evermark Journal`} />
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-border">
          <div className="container py-24 md:py-32">
            <div className="mb-10">
              <p className="overline text-muted-foreground">Continue reading</p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl">Related entries</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-x-6 gap-y-12" data-testid="journal-related">
              {related.map((r, i) => (
                <motion.article
                  key={r.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  data-testid={`journal-related-${r.slug}`}
                >
                  <Link to={`/journal/${r.slug}`} className="group block">
                    <div className="aspect-[4/5] overflow-hidden kb-parent bg-muted">
                      <img src={r.cover} alt={r.title} className="kb-img h-full w-full object-cover" />
                    </div>
                    <p className="mt-5 overline text-muted-foreground">{r.category} · {r.readTime}</p>
                    <h3 className="mt-3 font-display text-3xl leading-tight">{r.title}</h3>
                    <p className="mt-2 font-display italic text-muted-foreground">{r.subtitle}</p>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
